import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import { userAPI } from '../../api/endpoints';

const EditUserModal = ({ isOpen, onClose, onSubmit, user, loading }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState<Record<string, any>>({
    full_name: '',
    email: '',
    organization_name: '',
    sms_sender_id: '',
    tenant_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch organizations for super admin
  const { data: organizationsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await userAPI.getOrganizations();
      return response.data;
    },
    enabled: currentUser?.role === USER_ROLES.SUPER_ADMIN && isOpen && user?.role !== 'event_admin' && user?.role !== 'super_admin',
  });

  const organizations = organizationsData?.organizations || [];

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        organization_name: user.organization_name || '',
        sms_sender_id: user.sms_sender_id || '',
        sms_balance: user.sms_balance || 0,
        tenant_id: user.created_by || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Invalid email';
    if (!formData.full_name) newErrors.full_name = 'Full name is required';
    if (formData.sms_sender_id && formData.sms_sender_id.length > 11) {
      newErrors.sms_sender_id = 'Sender ID max 11 characters';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(user.id, formData);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">Edit User</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Input
              label="Full Name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              error={errors.full_name}
              required
            />

            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              required
            />

            {user?.role === 'event_admin' && (
              <Input
                label="Organization Name"
                name="organization_name"
                value={formData.organization_name}
                onChange={handleChange}
                error={errors.organization_name}
                placeholder="e.g. Acme Corp"
              />
            )}

            {currentUser?.role === USER_ROLES.SUPER_ADMIN && user?.role !== 'event_admin' && user?.role !== 'super_admin' && (
              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Organization Assignment</h3>
                <div>
                  <select
                    name="tenant_id"
                    value={formData.tenant_id}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select an organization...</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.organization_name || org.full_name} ({org.email})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Select the organization this user should belong to.
                  </p>
                </div>
              </div>
            )}

            {currentUser?.role === USER_ROLES.SUPER_ADMIN && user?.role === 'event_admin' && (
              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-900 mb-3">SMS Configuration (Super Admin Only)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="SMS Sender ID"
                    name="sms_sender_id"
                    value={formData.sms_sender_id}
                    onChange={handleChange}
                    error={errors.sms_sender_id}
                    placeholder="e.g. MyOrg (Max 11 chars)"
                  />
                  <Input
                    label="SMS Balance"
                    name="sms_balance"
                    type="number"
                    value={formData.sms_balance || 0}
                    onChange={handleChange}
                    error={errors.sms_balance}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
